import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router"
import { onboardingRoutes } from "@/router/onboarding"
import { contactRoutes } from "@/router/contacts"
import { getToken, getUser, clearStorage, hasUserPermission } from "@/shared/utils/helpers"
import { ResourceTypeName } from "@/shared/models/role"
import { isLoggingOut, isHandling401Error, setIsHandling401Error } from "@/shared/utils/auth-state"

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: "home",
        redirect: () => {
            if (getToken()) return { name: "contacts" }

            return { name: "login" }
        },
    },
    ...onboardingRoutes,
    ...contactRoutes,
    {
        path: "/:pathMatch(.*)",
        redirect: () => {
            if (getToken()) return { name: "contacts" }

            return { name: "login" }
        },
    },
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
})

router.afterEach(() => {
    sessionStorage.removeItem("alreadyRedirected")
})

router.beforeEach((to) => {
    // Prevent navigation while logging out or already handling session expiration
    if (isLoggingOut.value || isHandling401Error.value) {
        return
    }

    const token = getToken()
    const user = getUser()

    const toName = to.name as string

    // If going to login and already authenticated, redirect to home
    if (toName === "login" && token && user) {
        return { name: "home" }
    }

    // If route requires auth but user isn't authenticated
    if (to.meta.requiresAuth && (!token || !user)) {
        setIsHandling401Error(true) // Prevent loops during auth handling
        clearStorage()

        // Reset the flag after a delay to allow navigation to complete
        setTimeout(() => {
            setIsHandling401Error(false)
        }, 1000)

        return { name: "login", query: { redirect: encodeURIComponent(window.location.href) } }
    }

    if (!user?.role) {
        return
    }

    const hasPermission = hasUserPermission(user?.role, toName as ResourceTypeName)
    if (!hasPermission || (toName === "team" && user?.role?.name === "member")) {
        return { name: "home" }
    }
})

export default router
